import { mat4, vec2 } from "gl-matrix"

export interface HealingCoordinateTransformOptions {
	cropMatrix: Readonly<mat4>
	perspectiveMatrix: Readonly<mat4>
	outputSize: vec2
	renderLensDistortionCorrection: boolean
	lensDistortionCorrectionFactor: number
	lensImageSize: vec2
	lensImageCenter: vec2
	lensAutoscaleFactor: number
	lensNormalizationScale: number
	lensDistortionParams: [number, number, number, number]
	lensACMDistortionModel: boolean
	lensDistortion: number
	renderChromaticAberrationCorrection: boolean
	chromaticAberrationParams: number[] | null
}

export interface HealingCoordinateTransformContext extends HealingCoordinateTransformOptions {
	cacheKey: string
	nonlinear: boolean
}

export interface HealingCoordinateProjection {
	coord: vec2
	jacobianDeterminant: number
}

export interface HealingCoordinateJacobian {
	j00: number
	j01: number
	j10: number
	j11: number
	determinant: number
}

const MAX_NEWTON_ITERATIONS = 20
const MAX_UI_FALLBACK_ITERATIONS = 8

const isFinitePoint = (point: vec2) => Number.isFinite(point[0]) && Number.isFinite(point[1])

const applyManualLensDistortion = (
	coord: vec2,
	lensDistortion: number,
	lensImageSize: vec2,
): vec2 => {
	const center = 0.5
	const barrelSize = 0.5625
	let x = coord[0] - center
	let y = coord[1] - center
	let distortion = 1
	let zoom = 1

	if (lensDistortion > 0) {
		const c = Math.hypot(lensImageSize[0], lensImageSize[1]) / (lensDistortion * 4)
		const len = Math.hypot(x * lensImageSize[0], y * lensImageSize[1])
		const r = Math.max(len / c, 0.0001)
		distortion = Math.atan(r) / r
		const zoomR = Math.max(-0.5 * lensImageSize[0], -0.5 * lensImageSize[1]) / c
		zoom = Math.atan(zoomR) / zoomR
	}

	if (lensDistortion < 0) {
		const r = (x * x + y * y) * barrelSize
		const c = lensDistortion * -2
		distortion = 1 + r * c
		zoom = 1 + 0.5 * barrelSize * c
	}

	x = (x * distortion) / zoom
	y = (y * distortion) / zoom

	return [x + center, y + center]
}

const applyCameraLensCorrection = (
	coord: vec2,
	options: HealingCoordinateTransformOptions,
): vec2 | null => {
	const normalizationScale = options.lensNormalizationScale
	if (normalizationScale === 0) return null

	let x = coord[0] * options.lensImageSize[0] * normalizationScale - options.lensImageCenter[0]
	let y = coord[1] * options.lensImageSize[1] * normalizationScale - options.lensImageCenter[1]
	const scaledAutoscale =
		1 + options.lensDistortionCorrectionFactor * (options.lensAutoscaleFactor - 1)

	x *= scaledAutoscale
	y *= scaledAutoscale

	const [p0, p1, p2, p3] = options.lensDistortionParams
	if (options.lensACMDistortionModel) {
		const k1 = p0 * options.lensDistortionCorrectionFactor
		const k2 = p1 * options.lensDistortionCorrectionFactor
		const k3 = p2 * options.lensDistortionCorrectionFactor
		const k4 = p3 * options.lensDistortionCorrectionFactor
		const ru2 = x * x + y * y
		const ru4 = ru2 * ru2
		const common = 1 + k1 * ru2 + k2 * ru4 + k3 * ru4 * ru2 + 2 * k4 * y
		const ox = x * common
		const oy = y * common + k4 * ru2
		x = ox
		y = oy
	} else {
		const a = p0 * options.lensDistortionCorrectionFactor
		const b = p1 * options.lensDistortionCorrectionFactor
		const c = p2 * options.lensDistortionCorrectionFactor
		const ru2 = x * x + y * y
		const r = Math.sqrt(ru2)
		const polynomial = a * ru2 * r + b * ru2 + c * r + 1
		x *= polynomial
		y *= polynomial
	}

	return [
		(x + options.lensImageCenter[0]) / normalizationScale / options.lensImageSize[0],
		(y + options.lensImageCenter[1]) / normalizationScale / options.lensImageSize[1],
	]
}

const applyChromaticAberrationCorrection = (
	coord: vec2,
	options: HealingCoordinateTransformOptions,
): vec2 | null => {
	const params = options.chromaticAberrationParams
	if (!params || params.length < 6 || options.lensNormalizationScale === 0) return null

	const [vr = 0, vb = 0, cr = 0, cb = 0, br = 0, bb = 0] = params
	const normalizationScale = options.lensNormalizationScale
	const x = coord[0] * options.lensImageSize[0] * normalizationScale - options.lensImageCenter[0]
	const y = coord[1] * options.lensImageSize[1] * normalizationScale - options.lensImageCenter[1]
	const radiusSquared = x * x + y * y
	const radius = Math.sqrt(radiusSquared)
	const polynomialR =
		cr === 0 && cb === 0 ? br * radiusSquared + vr : br * radiusSquared + cr * radius + vr
	const polynomialB =
		cr === 0 && cb === 0 ? bb * radiusSquared + vb : bb * radiusSquared + cb * radius + vb
	const distortedR: vec2 = [x * polynomialR, y * polynomialR]
	const distortedB: vec2 = [x * polynomialB, y * polynomialB]
	const distortedG: vec2 = [
		(distortedR[0] + distortedB[0]) * 0.5,
		(distortedR[1] + distortedB[1]) * 0.5,
	]
	const inverseScaleX = 1 / normalizationScale / options.lensImageSize[0]
	const inverseScaleY = 1 / normalizationScale / options.lensImageSize[1]

	return [
		((distortedR[0] + distortedG[0] + distortedB[0]) / 3 + options.lensImageCenter[0]) *
			inverseScaleX,
		((distortedR[1] + distortedG[1] + distortedB[1]) / 3 + options.lensImageCenter[1]) *
			inverseScaleY,
	]
}

/** Mirrors healing-transform-combine's output-coordinate to stable-image-coordinate mapping. */
export const mapHealingOutputToImageCoord = (
	outputCoord: vec2,
	options: HealingCoordinateTransformOptions,
): vec2 | null => {
	const x = outputCoord[0] * 2 - 1
	const y = outputCoord[1] * 2 - 1
	const crop = options.cropMatrix
	const perspective = options.perspectiveMatrix
	const cropX = crop[0] * x + crop[4] * y + crop[12]
	const cropY = crop[1] * x + crop[5] * y + crop[13]
	const cropZ = crop[2] * x + crop[6] * y + crop[14]
	const cropW = crop[3] * x + crop[7] * y + crop[15]
	const perspectiveX =
		perspective[0] * cropX +
		perspective[4] * cropY +
		perspective[8] * cropZ +
		perspective[12] * cropW
	const perspectiveY =
		perspective[1] * cropX +
		perspective[5] * cropY +
		perspective[9] * cropZ +
		perspective[13] * cropW
	const perspectiveZ =
		perspective[2] * cropX +
		perspective[6] * cropY +
		perspective[10] * cropZ +
		perspective[14] * cropW
	const zFactor = 1 + perspectiveZ
	if (!Number.isFinite(zFactor) || Math.abs(zFactor) < 1e-6) return null

	let coord: vec2 = [(perspectiveX / zFactor) * 0.5 + 0.5, (perspectiveY / zFactor) * 0.5 + 0.5]
	coord = applyManualLensDistortion(coord, options.lensDistortion, options.lensImageSize)

	if (options.renderLensDistortionCorrection) {
		const corrected = applyCameraLensCorrection(coord, options)
		if (!corrected) return null
		coord = corrected
	}

	if (options.renderChromaticAberrationCorrection) {
		const corrected = applyChromaticAberrationCorrection(coord, options)
		if (!corrected) return null
		coord = corrected
	}

	return isFinitePoint(coord) ? coord : null
}

export const getHealingCoordinateJacobian = (
	point: vec2,
	options: HealingCoordinateTransformOptions,
): HealingCoordinateJacobian | null => {
	const maxOutputDimension = Math.max(options.outputSize[0], options.outputSize[1], 1)
	const step = Math.max(1e-5, 0.25 / maxOutputDimension)
	const x0 = mapHealingOutputToImageCoord([point[0] - step, point[1]], options)
	const x1 = mapHealingOutputToImageCoord([point[0] + step, point[1]], options)
	const y0 = mapHealingOutputToImageCoord([point[0], point[1] - step], options)
	const y1 = mapHealingOutputToImageCoord([point[0], point[1] + step], options)
	if (!x0 || !x1 || !y0 || !y1) return null

	const inverseStep = 1 / (2 * step)
	const j00 = (x1[0] - x0[0]) * inverseStep
	const j10 = (x1[1] - x0[1]) * inverseStep
	const j01 = (y1[0] - y0[0]) * inverseStep
	const j11 = (y1[1] - y0[1]) * inverseStep
	const determinant = j00 * j11 - j01 * j10
	if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-8) return null

	return { j00, j01, j10, j11, determinant }
}

export const projectHealingImageCoordToOutputWithJacobian = (
	target: vec2,
	options: HealingCoordinateTransformOptions,
	seed: vec2 = target,
): HealingCoordinateProjection | null => {
	let point: vec2 = [seed[0], seed[1]]
	const imageTolerance =
		0.05 /
		Math.max(
			options.lensImageSize[0],
			options.lensImageSize[1],
			options.outputSize[0],
			options.outputSize[1],
			1,
		)

	for (let iteration = 0; iteration < MAX_NEWTON_ITERATIONS; iteration++) {
		const mapped = mapHealingOutputToImageCoord(point, options)
		if (!mapped) return null
		const residualX = mapped[0] - target[0]
		const residualY = mapped[1] - target[1]
		const residual = Math.hypot(residualX, residualY)
		const jacobian = getHealingCoordinateJacobian(point, options)
		if (!jacobian) return null

		if (residual <= imageTolerance) {
			return { coord: point, jacobianDeterminant: jacobian.determinant }
		}

		const deltaX = (jacobian.j11 * residualX - jacobian.j01 * residualY) / jacobian.determinant
		const deltaY = (-jacobian.j10 * residualX + jacobian.j00 * residualY) / jacobian.determinant
		let accepted = false

		for (let damping = 1; damping >= 0.0625; damping *= 0.5) {
			const candidate: vec2 = [point[0] - deltaX * damping, point[1] - deltaY * damping]
			if (
				!isFinitePoint(candidate) ||
				Math.abs(candidate[0]) > 8 ||
				Math.abs(candidate[1]) > 8
			) {
				continue
			}
			const candidateMapped = mapHealingOutputToImageCoord(candidate, options)
			if (
				candidateMapped &&
				Math.hypot(candidateMapped[0] - target[0], candidateMapped[1] - target[1]) <
					residual
			) {
				point = candidate
				accepted = true
				break
			}
		}

		if (!accepted) return null
	}

	return null
}

/** Inverts the shader mapping for UI geometry that is stored in stable-image coordinates. */
export const projectHealingImageCoordToOutput = (
	target: vec2,
	context: HealingCoordinateTransformContext,
	seed?: vec2,
): vec2 | null => projectHealingImageCoordToOutputWithJacobian(target, context, seed)?.coord ?? null

/** Best-effort UI fallback that preserves the previous eight-step projection behavior. */
export const projectHealingImageCoordToOutputBestEffort = (
	target: vec2,
	context: HealingCoordinateTransformContext,
): vec2 | null => {
	let point: vec2 = [target[0], target[1]]
	let updated = false

	for (let iteration = 0; iteration < MAX_UI_FALLBACK_ITERATIONS; iteration++) {
		const mapped = mapHealingOutputToImageCoord(point, context)
		if (!mapped) break
		const candidate: vec2 = [point[0] + target[0] - mapped[0], point[1] + target[1] - mapped[1]]
		if (!isFinitePoint(candidate)) break
		point = candidate
		updated = true
	}

	return updated ? point : null
}

export const getHealingCoordinateTransformCacheKey = (options: HealingCoordinateTransformOptions) =>
	[
		...options.cropMatrix,
		...options.perspectiveMatrix,
		...options.outputSize,
		options.renderLensDistortionCorrection,
		options.lensDistortionCorrectionFactor,
		...options.lensImageSize,
		...options.lensImageCenter,
		options.lensAutoscaleFactor,
		options.lensNormalizationScale,
		...options.lensDistortionParams,
		options.lensACMDistortionModel,
		options.lensDistortion,
		options.renderChromaticAberrationCorrection,
		...(options.chromaticAberrationParams ?? []),
	].join(",")

export const createHealingCoordinateTransformContext = (
	options: HealingCoordinateTransformOptions,
): HealingCoordinateTransformContext => ({
	...options,
	cacheKey: getHealingCoordinateTransformCacheKey(options),
	nonlinear:
		options.lensDistortion !== 0 ||
		options.renderLensDistortionCorrection ||
		options.renderChromaticAberrationCorrection,
})
